/**
 * Locale-aware formatters used across tables, cards, KPIs.
 * Keep these pure — no Vue, no Pinia — so they can be tested in isolation.
 * The active locale arrives through `intlLocale()`, a plain function, for
 * exactly that reason.
 */
import { intlLocale } from '@/i18n'

/* Intl objects are expensive to build, so they are cached per locale rather
 * than per call — but keyed by locale, because the whole point of W3.M5 is
 * that "05 Jun 2026" becomes "05.06.2026 г." when the user switches to
 * Bulgarian. A module-level constant, which is what these were, silently
 * pinned every date in the product to en-GB. */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.DisplayNames>()

function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.DisplayNames>(
  key: string,
  make: () => T
): T {
  const locale = intlLocale()
  const id = `${locale}:${key}`
  let fmt = cache.get(id)
  if (!fmt) {
    fmt = make()
    cache.set(id, fmt)
  }
  return fmt as T
}

const numberFmt = () => memo('number', () => new Intl.NumberFormat(intlLocale()))
const dateFmt = () =>
  memo(
    'date',
    () => new Intl.DateTimeFormat(intlLocale(), { day: '2-digit', month: 'short', year: 'numeric' })
  )
const shortDateFmt = () =>
  memo('shortDate', () => new Intl.DateTimeFormat(intlLocale(), { day: '2-digit', month: 'short' }))

const regionFmt = () =>
  memo('region', () => new Intl.DisplayNames(intlLocale(), { type: 'region' }))

/**
 * A country code as a name a lawyer reads: `BG` → "Bulgaria", "България", …
 *
 * `EU` and the ISO "unknown" placeholder `XX` are named here because
 * `Intl.DisplayNames` has no entry for either — it would return the code back,
 * and the badge would read "EU EU". Anything else it cannot name falls back to
 * the code itself, which is still true.
 */
export function countryName(code: string | null | undefined): string {
  const raw = (code ?? '').trim().toUpperCase()
  if (!raw) return ''
  if (raw === 'EU') return 'European Union'
  if (raw === 'XX') return 'Unknown'
  if (raw.length !== 2) return raw
  try {
    return regionFmt().of(raw) ?? raw
  } catch {
    return raw
  }
}

export const formatNumber = (n) => (n == null ? '—' : numberFmt().format(n))

export const formatDate = (iso) => {
  if (!iso) return '—'
  const d = iso instanceof Date ? iso : new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateFmt().format(d)
}

/**
 * A calendar day (`YYYY-MM-DD`) as a date, the same day everywhere.
 *
 * `new Date('2026-09-16')` is midnight UTC, which a reader west of Greenwich
 * sees as the 15th. A report's day is a UTC day by definition, so it is read
 * at noon UTC — no time zone on Earth is twelve hours away from it.
 */
export const formatDay = (day: string | null | undefined) =>
  day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? formatDate(`${day}T12:00:00Z`) : formatDate(day)

export const formatShortDate = (iso) => {
  if (!iso) return '—'
  const d = iso instanceof Date ? iso : new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : shortDateFmt().format(d)
}

/**
 * Locale-aware text comparison, for every sort in the product.
 *
 * `'Ябълка' < 'Банан'` is true with the default `<`, because JavaScript
 * compares UTF-16 code units and Cyrillic has none of Latin's alphabetical
 * luck. A collator sorts Bulgarian correctly, ignores case and accents by
 * default here, and puts "10" after "9" thanks to numeric collation.
 */
const collatorFor = (locale: string) =>
  new Intl.Collator(locale, { sensitivity: 'base', numeric: true })
const collators = new Map<string, Intl.Collator>()

export function compareText(a: string | null | undefined, b: string | null | undefined): number {
  const locale = intlLocale()
  let c = collators.get(locale)
  if (!c) {
    c = collatorFor(locale)
    collators.set(locale, c)
  }
  return c.compare(a ?? '', b ?? '')
}

/** Sort a copy of `items` by a text key, correctly for the active locale. */
export function sortByText<T>(items: readonly T[], key: (item: T) => string): T[] {
  return [...items].sort((x, y) => compareText(key(x), key(y)))
}

export const formatPercent = (n, fractionDigits = 0) =>
  n == null ? '—' : `${Number(n).toFixed(fractionDigits)}%`

export const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
